import React, {useState, useEffect, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {Calculator, DollarSign, Percent} from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import {useTheme} from '../../../contexts/ThemeContext';
import {getToolById} from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './VATCalculatorPage/components/CalculationForm';
import ResultsOverview from './VATCalculatorPage/components/ResultsOverview';
import ComparisonTable from './VATCalculatorPage/components/ComparisonTable';
import QuickAdjustmentSidebar from './VATCalculatorPage/components/QuickAdjustmentSidebar';
import SavedCalculationsSidebar from './VATCalculatorPage/components/SavedCalculationsSidebar';
import TipsSidebar from './VATCalculatorPage/components/TipsSidebar';
import InfoContent from './VATCalculatorPage/components/InfoContent';
import VATRatesTable from './VATCalculatorPage/components/VATRatesTable';

// Определение начального состояния формы
export const initialFormData = {
    amount: '10000',
    vatRate: '20',
    calculationType: 'add', // 'add' - начисление НДС, 'extract' - выделение НДС
    precision: '2', // Количество знаков после запятой
    includeResults: {
        withVAT: true,
        withoutVAT: true,
        vatAmount: true
    },
    description: '', // Описание расчета для сохранения
};

// Определение доступных ставок НДС в России
export const vatRates = [
    {id: '20', name: 'НДС 20%', description: 'Основная ставка'},
    {id: '10', name: 'НДС 10%', description: 'Льготная ставка'},
    {id: '0', name: 'НДС 0%', description: 'Экспорт и особые случаи'},
    {id: 'none', name: 'Без НДС', description: 'Не облагается НДС'},
    {id: 'custom', name: 'Своя ставка', description: 'Произвольная ставка'}
];

/**
 * VATCalculatorPage Component - Калькулятор для расчета сумм с НДС и без НДС.
 */
const VATCalculatorPage = () => {
    const {subToolId} = useParams();
    const {darkMode, toggleDarkMode} = useTheme();

    // --- Данные инструмента ---
    const tool = useMemo(() => getToolById('vat-calculator'), []);
    const pageTitle = tool?.metaTitle || 'НДС калькулятор | Gensio';
    const metaDescription = tool?.metaDescription || 'Онлайн калькулятор НДС: расчет суммы с учетом и без учета НДС, выделение НДС из суммы. Поддержка различных ставок, сохранение расчетов и экспорт результатов.';

    // --- Управление состоянием ---
    const [formData, setFormData] = useState(initialFormData);
    const [results, setResults] = useState(null);
    const [errors, setErrors] = useState({});
    const [isCalculating, setIsCalculating] = useState(false);
    const [savedCalculations, setSavedCalculations] = useState([]);
    const [customVatRate, setCustomVatRate] = useState('');

    // --- Обновление заголовка и мета-описания ---
    useEffect(() => {
        document.title = pageTitle;
        let metaTag = document.querySelector('meta[name="description"]');
        if (!metaTag) {
            metaTag = document.createElement('meta');
            metaTag.name = 'description';
            document.head.appendChild(metaTag);
        }
        metaTag.setAttribute('content', metaDescription);
    }, [pageTitle, metaDescription]);

    // --- Загрузка сохраненных расчетов из localStorage ---
    useEffect(() => {
        const savedCalcsData = localStorage.getItem('savedVatCalculations');
        if (savedCalcsData) {
            try {
                setSavedCalculations(JSON.parse(savedCalcsData));
            } catch (e) {
                console.error("Error loading saved VAT calculations:", e);
            }
        }
    }, []);

    // --- Обработка subToolId для предварительной настройки формы ---
    useEffect(() => {
        if (!subToolId) return;

        setFormData(prev => {
            let newState = {...initialFormData};

            switch (subToolId) {
                case 'add':
                    newState.calculationType = 'add';
                    break;
                case 'extract':
                    newState.calculationType = 'extract';
                    break;
                case 'rate-10':
                    newState.vatRate = '10';
                    break;
                case 'rate-0':
                    newState.vatRate = '0';
                    break;
                case 'no-vat':
                    newState.vatRate = 'none';
                    break;
                default:
                    newState = {...prev};
            }
            return newState;
        });

        setResults(null);
        setErrors({});

    }, [subToolId]);

    // Сохранение расчета в localStorage
    const saveCalculation = (name) => {
        if (!results) return;

        const newSavedCalculation = {
            id: Date.now().toString(),
            name: name || `Расчет НДС для ${formData.amount}₽`,
            date: new Date().toISOString(),
            formData: {...formData},
            results: {...results}
        };

        const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
        setSavedCalculations(updatedSavedCalculations);
        localStorage.setItem('savedVatCalculations', JSON.stringify(updatedSavedCalculations));

        return newSavedCalculation;
    };

    // Удаление сохраненного расчета
    const deleteSavedCalculation = (id) => {
        const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
        setSavedCalculations(updatedSavedCalculations);
        localStorage.setItem('savedVatCalculations', JSON.stringify(updatedSavedCalculations));
    };

    // Загрузка сохраненного расчета
    const loadSavedCalculation = (id) => {
        const calcToLoad = savedCalculations.find(calc => calc.id === id);
        if (calcToLoad) {
            setFormData(calcToLoad.formData);
            setResults(calcToLoad.results);
        }
    };

    // --- RENDER ---
    return (
        <div
            className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
            {/* Background Pattern */}
            <div aria-hidden="true"
                 className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

            <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode}/>

            <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Breadcrumbs/>

                <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                    {/* --- Main Content Area --- */}
                    <div className="lg:w-2/3 flex-shrink-0">
                        {/* Page Header */}
                        <div className="mb-8">
                            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                                <Calculator className="w-8 h-8 text-green-600 dark:text-green-400 flex-shrink-0"/>
                                {tool?.name || 'Калькулятор НДС'}
                            </h1>
                            <p className="text-lg text-gray-600 dark:text-gray-400">
                                {metaDescription}
                            </p>
                        </div>

                        {/* --- Форма калькулятора --- */}
                        <CalculationForm
                            formData={formData}
                            setFormData={setFormData}
                            errors={errors}
                            setErrors={setErrors}
                            isCalculating={isCalculating}
                            setIsCalculating={setIsCalculating}
                            results={results}
                            setResults={setResults}
                            vatRates={vatRates}
                            saveCalculation={saveCalculation}
                            customVatRate={customVatRate}
                            setCustomVatRate={setCustomVatRate}
                        />

                        {/* --- Результаты расчета --- */}
                        {results && !isCalculating && (
                            <ResultsOverview results={results}/>
                        )}

                        {/* --- Сравнительная таблица ставок НДС --- */}
                        {results && !isCalculating && (
                            <ComparisonTable
                                amount={parseFloat(formData.amount)}
                                calculationType={formData.calculationType}
                                selectedRate={formData.vatRate}
                                customRate={formData.vatRate === 'custom' ? parseFloat(customVatRate) : null}
                            />
                        )}

                        {/* --- Таблица ставок НДС --- */}
                        <VATRatesTable/>

                        {/* --- Информационный контент о НДС --- */}
                        <InfoContent/>
                    </div>

                    {/* --- Sidebar Area --- */}
                    <aside className="lg:w-1/3 self-start space-y-8">
                        {/* --- Панель быстрой настройки --- */}
                        <QuickAdjustmentSidebar
                            formData={formData}
                            setFormData={setFormData}
                            errors={errors}
                            isCalculating={isCalculating}
                            vatRates={vatRates}
                            customVatRate={customVatRate}
                            setCustomVatRate={setCustomVatRate}
                        />

                        {/* --- Панель сохраненных расчетов --- */}
                        <SavedCalculationsSidebar
                            savedCalculations={savedCalculations}
                            deleteSavedCalculation={deleteSavedCalculation}
                            loadSavedCalculation={loadSavedCalculation}
                        />

                        {/* --- Панель полезных советов --- */}
                        <TipsSidebar/>

                        {/* --- Связанные инструменты --- */}
                        {tool && (
                            <ToolSidebar
                                currentToolId={tool.id}
                                currentSubToolId={subToolId}
                                tool={tool}
                            />
                        )}
                    </aside>
                </div>
            </main>

            <Footer/>

            {/* --- Global Styles --- */}
            <style jsx global>{`
                /* Base Input Styles */
                .form-input, .form-select, .form-textarea {
                    @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
                }

                .form-checkbox {
                    @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
                }

                .form-radio {
                    @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
                }

                /* Error State Styling */
                .input-error {
                    @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
                }
            `}</style>
        </div>
    );
};

export default VATCalculatorPage;